import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Download, Search, ShieldCheck, LogIn, LogOut, FileEdit, Trash2, Settings, Mail, AlertTriangle } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, Btn, formatDate } from './shared';
import { useToast } from '../../hooks/use-toast';

/**
 * AdminAuditLog — read-only viewer for db.audit_log.
 *
 * Every administrative mutation that calls audit.record on the backend
 * shows up here: logins (success + failure), lead status changes,
 * deletions, page edits, settings + SMTP changes. Filter by action or
 * actor, export the whole trail to CSV for compliance.
 */
const ACTION_META = {
  'auth.login':            { Icon: LogIn,       tone: 'emerald', label: 'Login' },
  'auth.login_failed':     { Icon: AlertTriangle, tone: 'rose',  label: 'Login failed' },
  'auth.logout':           { Icon: LogOut,      tone: 'slate',   label: 'Logout' },
  'lead.status_changed':   { Icon: FileEdit,    tone: 'violet',  label: 'Lead status' },
  'lead.deleted':          { Icon: Trash2,      tone: 'rose',    label: 'Lead deleted' },
  'page.updated':          { Icon: FileEdit,    tone: 'violet',  label: 'Page edit' },
  'settings.updated':      { Icon: Settings,    tone: 'amber',   label: 'Settings' },
  'smtp.updated':          { Icon: Mail,        tone: 'amber',   label: 'SMTP' },
};

const TONE = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rose:    'bg-rose-50 text-rose-700 border-rose-200',
  violet:  'bg-violet-50 text-violet-700 border-violet-200',
  amber:   'bg-amber-50 text-amber-700 border-amber-200',
  slate:   'bg-slate-100 text-slate-600 border-slate-200',
};

export default function AdminAuditLog() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (actionFilter) params.action = actionFilter;
      if (query) params.actor = query;
      const res = await api.get('/api/admin/audit-log', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast({ title: 'Failed to load audit log', description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [actionFilter, query, toast]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get('/api/admin/audit-log/export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'voipinger-audit-log.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Export failed', description: formatApiError(err) });
    }
  };

  const ACTIONS = ['', 'auth.login', 'auth.login_failed', 'lead.status_changed', 'lead.deleted', 'page.updated', 'settings.updated', 'smtp.updated'];

  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-2"><ShieldCheck size={18} /> Audit log</span>}
        subtitle={`${total} recorded events — who did what, when, from where`}
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
            <Btn onClick={exportCsv}><Download size={14} /> Export CSV</Btn>
          </div>
        }
      />

      <AdminCard>
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by actor email…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-500/30 outline-none text-white text-sm placeholder:text-slate-500"
            />
          </div>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="md:w-56 px-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 outline-none text-white text-sm"
          >
            {ACTIONS.map((a) => (
              <option key={a || 'all'} value={a}>{a ? (ACTION_META[a]?.label || a) : 'All actions'}</option>
            ))}
          </select>
        </div>

        {loading ? <Loading /> : items.length === 0 ? (
          <Empty>No audit events match these filters.</Empty>
        ) : (
          <div className="space-y-1">
            {items.map((it) => {
              const meta = ACTION_META[it.action] || { Icon: FileEdit, tone: 'slate', label: it.action };
              return (
                <div key={it.id} className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0">
                  <span className={`mt-0.5 inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-bold ${TONE[meta.tone]}`}>
                    <meta.Icon size={11} /> {meta.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{it.summary || it.action}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {it.actor_email || 'anonymous'}
                      {it.ip ? ` · ${it.ip}` : ''}
                      {it.target_type ? ` · ${it.target_type}${it.target_id ? `:${it.target_id}` : ''}` : ''}
                    </p>
                  </div>
                  <span className="text-[11px] text-slate-500 flex-shrink-0">{formatDate(it.at)}</span>
                </div>
              );
            })}
          </div>
        )}
      </AdminCard>
    </>
  );
}
